import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, CreditCard, Mail, LogOut, User } from 'lucide-react';
import PageTitle from '../hooks/PageTitle';

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { icon: FileText, label: 'My Documents', count: 0, color: 'bg-blue-500' },
    { icon: Calendar, label: 'Appointments', count: 0, color: 'bg-green-500' },
    { icon: CreditCard, label: 'Invoices', count: 0, color: 'bg-purple-500' },
    { icon: Mail, label: 'Messages', count: 0, color: 'bg-red-500' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageTitle title="Client Dashboard" />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif text-gray-900">Client Portal</h1>
              <p className="text-gray-600 mt-1">Welcome, {user?.name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg shadow-lg p-8 text-white mb-8">
          <h2 className="text-2xl font-serif mb-2">Welcome to Your Client Portal</h2>
          <p className="text-gray-300">
            Access your documents, manage your appointments and stay updated on your cases.
          </p>
        </div>

        {/* User Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-gray-900 w-16 h-16 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900">{user?.name}</h3>
              <p className="text-gray-600">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t">
            <div>
              <p className="text-sm text-gray-600">Account Status</p>
              <p className="font-medium text-green-600">Active</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Account Type</p>
              <p className="font-medium capitalize">{user?.role}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Client Since</p>
              <p className="font-medium">{new Date(user?.created_at).toLocaleDateString('en-GB')}</p>
            </div>
          </div>
        </div>

        {/* Menu Grid */}
        <h2 className="text-2xl font-serif text-gray-900 mb-6">Available Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow text-left relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`${item.color} p-3 rounded-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  {item.count > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {item.count}
                    </span>
                  )}
                </div>
                <h3 className="font-medium text-gray-900 mb-1">{item.label}</h3>
                <p className="text-sm text-gray-600">Visualizza tutti</p>
              </button>
            );
          })}
        </div>

        {/* Recent Activity */}
        <h2 className="text-2xl font-serif text-gray-900 mb-6">Attività Recente</h2>
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No recent activity</p>
            <p className="text-sm mt-2">Your documents and updates will appear here</p>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="font-medium text-gray-900 mb-2">Need Help?</h3>
          <p className="text-sm text-gray-600 mb-4">
            Our team is available to assist you. Contact your relationship manager for any questions.
          </p>
          <button className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm">
            Contact Team
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;